import { useCallback, useMemo, useState } from 'react';

import type { IdDaSecao, ItemDoCatalogo } from './catalogo';
import { designSystemSections, type DesignSystemSection } from './sectionRegistry';

export interface ResultadoDaBusca extends ItemDoCatalogo {
  secao: IdDaSecao;
  rotuloDaSecao: string;
}

/** Sem acento e em minúsculas: "Competência" acha "competencia" e vice-versa. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Procura nos itens do registro de seções. Cada palavra da consulta precisa
 * aparecer no título, nas palavras-chave ou nos nomes de componente do item —
 * "copiar cpf" acha o item das variantes do `CopyableText`, não tudo que fala
 * de copiar.
 */
export function buscarNaDocumentacao(
  consulta: string,
  secoes: readonly DesignSystemSection[] = designSystemSections,
): ResultadoDaBusca[] {
  const palavras = normalizar(consulta).split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return [];
  return secoes.flatMap((secao) =>
    secao.itens
      .filter((item) => {
        const onde = normalizar(
          [item.titulo, ...item.palavrasChave, ...item.componentes].join(' '),
        );
        return palavras.every((p) => onde.includes(p));
      })
      .map((item) => ({ ...item, secao: secao.id, rotuloDaSecao: secao.label })),
  );
}

/** Quantos quadros de animação esperar a seção montar antes de desistir de rolar. */
const QUADROS_DE_ESPERA = 30;

/**
 * Busca da documentação do design system, derivada do registro de seções
 * (`sectionRegistry.tsx` → `catalogo.ts`). Não há índice escrito à mão: o
 * item que a tela mostra é o item que a busca acha, e ele leva à própria
 * âncora (`data-search-id`).
 */
export function useBuscaNaDocumentacao() {
  const [consulta, definirConsulta] = useState('');

  const resultados = useMemo(() => buscarNaDocumentacao(consulta), [consulta]);

  const contagemPorSecao = useMemo(() => {
    const contagem: Partial<Record<IdDaSecao, number>> = {};
    for (const r of resultados) contagem[r.secao] = (contagem[r.secao] ?? 0) + 1;
    return contagem;
  }, [resultados]);

  /**
   * Rola até a âncora do item e a realça por um instante. A seção acabou de
   * ser trocada, então espera ela montar (alguns quadros) antes de procurar.
   */
  const rolarAte = useCallback((id: string) => {
    let quadros = 0;
    const procurar = () => {
      const alvo = document.querySelector<HTMLElement>(`[data-search-id="${CSS.escape(id)}"]`);
      if (!alvo) {
        quadros += 1;
        if (quadros < QUADROS_DE_ESPERA) requestAnimationFrame(procurar);
        return;
      }
      alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      alvo.classList.add('ring-2', 'ring-ring', 'ring-offset-2');
      window.setTimeout(() => alvo.classList.remove('ring-2', 'ring-ring', 'ring-offset-2'), 1500);
    };
    requestAnimationFrame(procurar);
  }, []);

  return { consulta, definirConsulta, resultados, contagemPorSecao, rolarAte };
}
